import type { AdminNavSection } from './adminNav';
import { ADMIN_NAV_SECTIONS } from './adminNav';

export type AdminScope = 'full' | 'limited';

/** First landing page after login for each scope */
export const ADMIN_HOME_PATH: Record<AdminScope, string> = {
  full: '/dashboard',
  limited: '/users',
};

const LIMITED_ADMIN_PATH_PREFIXES = [
  '/users',
  '/behavior',
  '/teachers',
  '/sessions',
  '/page-editor',
  '/files',
  '/playbacks',
  '/exercises',
  '/formations',
  '/analytics',
];

/** Paths marked superAdminOnly in adminNav — require scope full. */
export const SUPER_ADMIN_ONLY_PATHS = ADMIN_NAV_SECTIONS.flatMap((section) =>
  section.items.filter((item) => item.superAdminOnly).map((item) => item.path),
);

function normalizeAdminPath(path: string): string {
  return path.replace(/\/$/, '') || '/';
}

function isSuperAdminOnlyPath(path: string): boolean {
  const normalized = normalizeAdminPath(path);
  return SUPER_ADMIN_ONLY_PATHS.some(
    (prefix) => normalized === prefix || normalized.startsWith(`${prefix}/`),
  );
}

export function isPathAllowedForAdminScope(path: string, scope: AdminScope): boolean {
  const normalized = normalizeAdminPath(path);

  if (isSuperAdminOnlyPath(normalized)) {
    return scope === 'full';
  }

  if (scope === 'full') {
    return true;
  }

  return LIMITED_ADMIN_PATH_PREFIXES.some(
    (prefix) => normalized === prefix || normalized.startsWith(`${prefix}/`),
  );
}

export function filterNavSections(sections: AdminNavSection[], scope: AdminScope): AdminNavSection[] {
  if (scope === 'full') {
    return sections;
  }

  return sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => !item.superAdminOnly),
    }))
    .filter((section) => section.items.length > 0);
}

export function getNavSectionsForScope(scope: AdminScope): AdminNavSection[] {
  return filterNavSections(ADMIN_NAV_SECTIONS, scope);
}
